import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { plansAPI, employeesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const Plans = () => {
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const [plans, setPlans] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    plan_name: '', description: '', customer: '',
    manager_id: '', priority: 'Medium',
    start_date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [p, e] = await Promise.allSettled([plansAPI.getAll(), employeesAPI.getAll()]);
      if (p.status === 'fulfilled') setPlans(p.value.data);
      if (e.status === 'fulfilled') setEmployees(e.value.data);
    } catch (err) {
      console.error('Error loading plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (saving) return;
    try {
      setSaving(true);
      const res = await plansAPI.create({
        ...form,
        manager_id: form.manager_id || null,
        created_by: user?.user_id,
        created_by_name: user?.name,
      });
      setShowModal(false);
      navigate(`/plans/${res.data.plan_id}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Could not create the plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`Delete plan "${plan.plan_name}"? This cannot be undone.`)) return;
    try {
      await plansAPI.delete(plan.plan_id);
      load();
    } catch (err) {
      alert('Could not delete the plan');
    }
  };

  const change = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  if (loading) return <div className="loading">Loading plans…</div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2 className="page-title">Project Plans</h2>
          <p className="page-subtitle">
            Draft a plan with dependencies, then turn it into a live project
          </p>
        </div>
        {can('createEditProject') && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            ➕ New Plan
          </button>
        )}
      </div>

      <div className="card">
        {plans.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🗂️</div>
            <div className="empty-state-text">No plans yet</div>
            <p>Create a plan to sketch out tasks and dependencies before committing.</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Customer</th>
                  <th>Manager</th>
                  <th>Start</th>
                  <th>Tasks</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {plans.map(p => (
                  <tr key={p.plan_id}>
                    <td>
                      <Link to={`/plans/${p.plan_id}`}><strong>{p.plan_name}</strong></Link>
                      {p.description && (
                        <div style={{ fontSize: 12, color: '#7f8c8d', marginTop: 4 }}>
                          {p.description.length > 70 ? p.description.slice(0, 70) + '…' : p.description}
                        </div>
                      )}
                    </td>
                    <td>{p.customer || '—'}</td>
                    <td>{p.manager_name || '—'}</td>
                    <td>{p.start_date}</td>
                    <td>{p.task_count}</td>
                    <td>
                      {p.status === 'Created' ? (
                        <span className="badge badge-success">✓ Created</span>
                      ) : (
                        <span className="badge badge-warning">Draft</span>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <Link to={`/plans/${p.plan_id}`} className="btn btn-sm btn-primary" style={{ marginRight: 8 }}>
                        {p.status === 'Created' ? 'View' : 'Edit'}
                      </Link>
                      {p.status === 'Created' && p.created_project_id && (
                        <Link to={`/projects/${p.created_project_id}`} className="btn btn-sm btn-success" style={{ marginRight: 8 }}>
                          Project
                        </Link>
                      )}
                      {can('createEditProject') && p.status !== 'Created' && (
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(p)}>
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">New Project Plan</h3>
            </div>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label className="form-label">Plan / Project Name *</label>
                <input name="plan_name" className="form-control" value={form.plan_name}
                  onChange={change} required placeholder="e.g. Website Rebuild" />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea name="description" className="form-control" value={form.description}
                  onChange={change} placeholder="What is this project about?" />
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Customer</label>
                  <input name="customer" className="form-control" value={form.customer} onChange={change} />
                </div>
                <div className="form-group">
                  <label className="form-label">Manager</label>
                  <select name="manager_id" className="form-control" value={form.manager_id} onChange={change}>
                    <option value="">— None —</option>
                    {employees.map(e => (
                      <option key={e.employee_id} value={e.employee_id}>{e.employee_name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select name="priority" className="form-control" value={form.priority} onChange={change}>
                    <option>Low</option><option>Medium</option><option>High</option><option>Critical</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Plan Start Date *</label>
                  <input type="date" name="start_date" className="form-control"
                    value={form.start_date} onChange={change} required />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Creating…' : 'Create Plan →'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Plans;
